var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import atbay from '../assets/At-Bay_Logo.png';
import cfc from '../assets/CFC_Logo.png';
import tokiomarine from '../assets/TokioMarine_Logo.png';
/**
 * Recursively traverses a nested list of questions (and their sub-questions)
 * and executes a callback function for each question.
 *
 * The callback can optionally return `{ isBreak: true }` to stop traversal early.
 *
 * Example use cases:
 * - Searching for a specific question by key
 * - Modifying all questions in a deeply nested structure
 * - Collecting specific data from each question node
 *
 * @param questions - The array of question objects to traverse.
 * @param callback - A function executed for each question.
 *   Receives the current question and its parent question key (if any).
 *   Return `{ isBreak: true }` from the callback to stop recursion.
 */
export var forEachQuestionDeep = function (questions, callback, values) {
    if (callback === void 0) { callback = function () { }; }
    if (!questions || !Array.isArray(questions))
        return;
    var recursive = function (q, parentQuestionKey) {
        var _a;
        var res = callback === null || callback === void 0 ? void 0 : callback(q, parentQuestionKey);
        if (res && res.isBreak) {
            return res; // Stop if callback requests a break
        }
        var repeatKeys = [''];
        if (q.inputType === 'group' && q.repeatBy) {
            var value = values[q.repeatBy.targetKey];
            var config = questions.find(function (ques) { var _a; return ques.questionKey === ((_a = q.repeatBy) === null || _a === void 0 ? void 0 : _a.targetKey); });
            if ((config === null || config === void 0 ? void 0 : config.inputType) === 'number') {
                if (typeof value === 'number' && value > -1) {
                    repeatKeys = Array.from({ length: value }).map(function (_, i) {
                        return (i + 1).toString();
                    });
                }
                else {
                    repeatKeys = [];
                }
            }
            else if ((config === null || config === void 0 ? void 0 : config.inputType) === 'multiselect') {
                if (Array.isArray(value)) {
                    repeatKeys = value;
                }
                else {
                    repeatKeys = [];
                }
            }
        }
        if (q.inputType === 'group' && ((_a = q.questions) === null || _a === void 0 ? void 0 : _a.length) && repeatKeys.length) {
            for (var j = 0; j < repeatKeys.length; j++) {
                var repeatKey = repeatKeys[j];
                var questionKey = q.questionKey;
                if (repeatKey) {
                    questionKey = "".concat(q.questionKey, "_").concat(repeatKey);
                }
                for (var i = 0; i < q.questions.length; i++) {
                    var res_1 = recursive(q.questions[i], parentQuestionKey
                        ? "".concat(parentQuestionKey, ".").concat(questionKey)
                        : questionKey);
                    if (res_1 && res_1.isBreak) {
                        return res_1; // Stop recursion when flagged
                    }
                }
            }
        }
    };
    for (var i = 0; i < questions.length; i++) {
        var res = recursive(questions[i]);
        if (res && res.isBreak) {
            break;
        }
    }
};
/**
 * Checks if a value is an Address object by looking for common address-related keys.
 *
 * @param {*} value The value to check.
 * @returns {boolean} True if the value is an Address object.
 */
export function isAddressObject(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    return ('street' in value ||
        'city' in value ||
        'postalCode' in value ||
        'region' in value);
}
export function searchValueRecursively(ob, searchKey) {
    for (var i in ob) {
        if (!ob.hasOwnProperty(i))
            continue;
        if (typeof ob[i] == 'object' && ob[i] !== null) {
            if (isAddressObject(ob[i])) {
                if (searchKey === i) {
                    return { value: ob[i], key: i };
                }
            }
            else {
                var data = searchValueRecursively(ob[i], searchKey);
                if (data !== undefined && data.key) {
                    return __assign(__assign({}, data), { key: "".concat(i, ".").concat(data.key) });
                }
            }
        }
        else {
            if (searchKey === i) {
                return { value: ob[i], key: i };
            }
        }
    }
}
export function flattenObject(ob) {
    var toReturn = {};
    for (var i in ob) {
        if (!ob.hasOwnProperty(i))
            continue;
        if (typeof ob[i] == 'object' && ob[i] !== null) {
            var flatObject = flattenObject(ob[i]);
            for (var x in flatObject) {
                if (!flatObject.hasOwnProperty(x))
                    continue;
                toReturn[i + '.' + x] = flatObject[x];
            }
        }
        else {
            toReturn[i] = ob[i];
        }
    }
    return toReturn;
}
export var regroupAnswers = function (productQuestionSet, answers) {
    if (!productQuestionSet || !answers) {
        return answers;
    }
    var result = {};
    var helper = function (questionsForSection, answersForSection) {
        var regroupedData = _.cloneDeep(answersForSection);
        var hoistedGroups = questionsForSection.filter(function (q) { return !!q.originalPath; });
        hoistedGroups.sort(function (a, b) { return a.originalPath.length - b.originalPath.length; });
        for (var _i = 0, hoistedGroups_1 = hoistedGroups; _i < hoistedGroups_1.length; _i++) {
            var group = hoistedGroups_1[_i];
            var groupKey = group.questionKey;
            var destinationPath = "".concat(group.originalPath, ".").concat(groupKey);
            var groupData = regroupedData[groupKey];
            if (groupData) {
                _.set(regroupedData, destinationPath, groupData);
                delete regroupedData[groupKey];
            }
        }
        return regroupedData;
    };
    var answerSetParts = Object.keys(answers);
    answerSetParts.forEach(function (currentKey) {
        var currentQuestionSet = productQuestionSet[currentKey];
        var currentAnswerSet = answers[currentKey];
        if (currentQuestionSet && currentAnswerSet) {
            result[currentKey] = helper(currentQuestionSet.questions, currentAnswerSet);
        }
        else {
            result[currentKey] = currentAnswerSet;
        }
    });
    return result;
};
export var flattenNestedGroupAnswers = function (requiredParams) {
    if (!requiredParams || !Object.keys(requiredParams).length) {
        return requiredParams;
    }
    var result = {};
    var helper = function (group, level) {
        if (level === void 0) { level = 0; }
        var currentResult = {
            spread: [],
            data: {},
            level: level
        };
        var questionKeys = Object.keys(group);
        questionKeys.forEach(function (key) {
            var _a;
            var answer = group[key];
            if (typeof answer === 'object' &&
                answer !== null &&
                answer !== undefined &&
                !Array.isArray(answer) &&
                Object.keys(answer).length) {
                var val = helper(answer, level + 1);
                if (val.spread.length) {
                    Object.assign(currentResult.data, val.spread.reduce(function (prev, curr) { return Object.assign(prev, curr); }, {}));
                }
                if (level > 0) {
                    if (!Object.keys(val.data).some(function (k) {
                        return [
                            'city',
                            'county',
                            'region',
                            'street',
                            'country',
                            'secondary',
                            'postalCode',
                        ].includes(k);
                    })) {
                        currentResult.level = val.level;
                        Object.assign(currentResult.data, val.data);
                    }
                    else {
                        currentResult.spread.push((_a = {}, _a[key] = val.data, _a));
                    }
                }
                else {
                    currentResult.level = val.level;
                    currentResult.data[key] = val.data;
                }
            }
            else {
                currentResult.data[key] = answer;
            }
        });
        if (level === 0 && currentResult.level > 2) {
            return helper(currentResult.data);
        }
        else {
            return currentResult;
        }
    };
    var sections = Object.keys(requiredParams);
    sections.forEach(function (section) {
        var currentSection = requiredParams[section];
        if (!currentSection || !Object.keys(currentSection).length) {
            return;
        }
        result[section] = helper(currentSection).data;
    });
    return result;
};
export var flattenNestedGroupQuestion = function (productQuestionSet) {
    if (!productQuestionSet) {
        return productQuestionSet;
    }
    var result = {};
    var helper = function (questions, currentPath) {
        var finalQuestionList = [];
        for (var _i = 0, questions_1 = questions; _i < questions_1.length; _i++) {
            var question = questions_1[_i];
            if (question.inputType !== 'group' || !question.questions) {
                finalQuestionList.push(question);
                continue;
            }
            var newPath = currentPath
                ? "".concat(currentPath, ".").concat(question.questionKey)
                : question.questionKey;
            var childrenToKeep = [];
            var childrenToHoist = [];
            var processedChildren = helper(question.questions, newPath);
            for (var _a = 0, processedChildren_1 = processedChildren; _a < processedChildren_1.length; _a++) {
                var child = processedChildren_1[_a];
                if (child.inputType === 'group') {
                    if (!child.originalPath) {
                        childrenToHoist.push(__assign(__assign({}, child), { originalPath: newPath }));
                    }
                    else {
                        childrenToHoist.push(__assign({}, child));
                    }
                }
                else {
                    childrenToKeep.push(child);
                }
            }
            var modifiedParentGroup = __assign(__assign({}, question), { questions: childrenToKeep });
            finalQuestionList.push(modifiedParentGroup);
            finalQuestionList.push.apply(finalQuestionList, childrenToHoist);
        }
        return finalQuestionList;
    };
    var questionSetParts = Object.keys(productQuestionSet);
    questionSetParts.forEach(function (currentKey) {
        var currentSet = productQuestionSet[currentKey];
        result[currentKey] = __assign(__assign({}, currentSet), { questions: helper(currentSet.questions, '') });
    });
    return result;
};
export var providerNameMapper = {
    atbay: {
        value: React.createElement("img", { src: atbay, alt: "atbay", className: "h-full" }),
        type: 'img'
    },
    cfc: {
        value: React.createElement("img", { src: cfc, alt: "cfc", className: "h-full" }),
        type: 'img'
    },
    tmhcc: {
        value: React.createElement("img", { src: tokiomarine, alt: "tokiomarine", className: "h-full" }),
        type: 'img'
    }
};
export var safeStringRegex = new RegExp(/^[A-Za-z0-9\-_()$@%.,:;/+&'#\s]*(?:[<>])?[A-Za-z0-9\-()$@%.,:;/+&'#\s]*$/);
export var removeUnderScore = function (title) {
    return title.replace(/_+/g, ' ').trim();
};
export var mergeHelperAndHint = function (helper, hint) {
    var hintArray = Array.isArray(hint) ? hint : hint ? [hint] : [];
    return helper ? __spreadArray([helper], hintArray, true) : hintArray;
};
export var formatDate = function (date) {
    return String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0') +
        '-' +
        date.getFullYear();
};
export var getGroupQuestion = function (removedQuestions, questionKey, questions) {
    var filtered = questions === null || questions === void 0 ? void 0 : questions.filter(function (innerQues) {
        return !removedQuestions.includes("".concat(questionKey, ".").concat(innerQues.questionKey));
    });
    return filtered;
};
export var getMatchedKey = function (allKeys, key) {
    var eKey = null;
    allKeys.some(function (k) {
        if (new RegExp("^".concat(k, "(_d+)?$")).test(key)) {
            eKey = k;
            return true;
        }
        return false;
    });
    return eKey;
};
export var renderQuestionLabel = function (isHtml, label) {
    return isHtml ? (React.createElement("div", { dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(label) } })) : (label);
};
function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}
/**
 * Deep merges two objects.
 * @param target The base object.
 * @param source The object with priority (overrides target).
 */
export function deepMerge(target, source) {
    var output = __assign({}, target);
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(function (key) {
            var targetValue = target[key];
            var sourceValue = source[key];
            if (isObject(targetValue) && isObject(sourceValue)) {
                output[key] = deepMerge(targetValue, sourceValue);
            }
            else {
                output[key] = sourceValue;
            }
        });
    }
    return output;
}
